/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { Suspense, useEffect } from "react";

import { useNavigate } from "react-router";

import { useHomeRoute } from "@/routes/hooks/useHomeRoute";
import { useNavigationStore } from "@/stores/useNavigationStore";

import { useAuth } from "../../../../context/Auth/AuthProvider";
import { ROUTES, useMediaQueries } from "../../../../shared";
import RectangleLoader from "../../../../shared/components/loaders/RectangleLoader";
import { useReleaseFeatureToggle } from "../../../../shared/featureToggles/hooks";
import { FEATURE_NAMES } from "../../../../shared/featureToggles/types";
import { useStyles as sharedStyles } from "../../../../shared/styles/sharedStyles";
import Logo from "../../../Logo/Logo";
import MobileMenuButton from "../../../MobileMenuButton/MobileMenuButton";
import { useStyles } from "../../styles";
import CompanySelector from "../CompanySelector/CompanySelector";
import Controls from "../Controls";
import FindDevice from "../FindDevice/FindDevice";

interface NavbarHeaderProps {
  route: ROUTES;
}

const NavbarHeader = ({ route }: NavbarHeaderProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { fromMd } = useMediaQueries();
  const { pendingRoute, setPendingRoute, setTargetRoute } =
    useNavigationStore();

  const { homeRoute } = useHomeRoute();

  const { isReleaseFeatureEnabledForRole } = useReleaseFeatureToggle();

  const { header, headerContent, navbarItemsBlock } = useStyles();
  const { logo } = sharedStyles();

  const isAllowedToViewCompanySelector = isReleaseFeatureEnabledForRole(
    FEATURE_NAMES.ShowCompanySelector,
    user?.role
  );

  const isAllowedToFindDevice = isReleaseFeatureEnabledForRole(
    FEATURE_NAMES.ShowFindDevice,
    user?.role
  );

  // fix for infinite load when token expires
  useEffect(() => {
    if (pendingRoute && user) {
      navigate(pendingRoute);
      setPendingRoute(null);
      setTargetRoute(null);
    }
  }, [pendingRoute, user, navigate, setPendingRoute, setTargetRoute]);

  return (
    <header data-testid="navbar-header" css={css(header)}>
      <div css={css(headerContent)}>
        <div css={css({ display: "flex" })}>
          <Logo
            css={css([logo, { padding: "0 8px 0 0" }])}
            onClick={() => navigate(homeRoute)}
          />
        </div>
        <div css={css(navbarItemsBlock)}>
          {isAllowedToFindDevice && <FindDevice />}
          {isAllowedToViewCompanySelector && (
            <Suspense fallback={<RectangleLoader height={24} width={150} />}>
              <CompanySelector route={route} />
            </Suspense>
          )}

          {fromMd ? <Controls /> : <MobileMenuButton />}
        </div>
      </div>
    </header>
  );
};

export default NavbarHeader;
